/**
 * Fast sliding-block search for the escape puzzle (GDD §8.8). The board is 6×6, block 0 is
 * the getaway car (length 2, horizontal, row 2) and must reach the right edge. States are
 * arrays of axis positions in a flat typed array, so a search of tens of thousands of nodes
 * takes a few milliseconds.
 */

export const SIZE = 6;
export const CAR_ROW = 2;

export interface Block {
  id: number;
  x: number;
  y: number;
  len: number;
  horiz: boolean;
  police: boolean;
}

export interface EscapeMove {
  block: number;
  delta: number;
}

export type SolveResult =
  | { status: 'solved'; moves: number; path: EscapeMove[] }
  | { status: 'unsolvable' }
  | { status: 'unknown' };

export function cellsOf(b: Block): { x: number; y: number }[] {
  return Array.from({ length: b.len }, (_, i) =>
    b.horiz ? { x: b.x + i, y: b.y } : { x: b.x, y: b.y + i },
  );
}

export function insideBoard(b: Block): boolean {
  return b.x >= 0 && b.y >= 0 && (b.horiz ? b.x + b.len <= SIZE : b.y + b.len <= SIZE);
}

/** True when `b` is on the board and overlaps none of `blocks` (ignoring `b.id` itself). */
export function fits(blocks: readonly Block[], b: Block): boolean {
  if (!insideBoard(b)) return false;
  const occ = new Set<number>();
  for (const o of blocks) {
    if (o.id === b.id) continue;
    for (const c of cellsOf(o)) occ.add(c.y * SIZE + c.x);
  }
  return cellsOf(b).every((c) => !occ.has(c.y * SIZE + c.x));
}

export function slid(b: Block, delta: number): Block {
  return b.horiz ? { ...b, x: b.x + delta } : { ...b, y: b.y + delta };
}

/** Legal slides of one block: every delta whose whole path is free. */
export function legalSlides(blocks: readonly Block[], id: number): number[] {
  const b = blocks.find((x) => x.id === id);
  if (!b) return [];
  const out: number[] = [];
  for (const sign of [1, -1]) {
    for (let d = 1; d <= SIZE; d++) {
      const moved = slid(b, sign * d);
      if (!fits(blocks, moved)) break;
      out.push(sign * d);
    }
  }
  return out;
}

export function carSolved(blocks: readonly Block[]): boolean {
  const car = blocks.find((b) => b.id === 0);
  return !!car && car.x + car.len === SIZE;
}

/**
 * Breadth-first search for the fewest moves (a slide of any length is one move).
 * `unknown` means the node cap was hit before an answer was found.
 */
export function solveBlocks(blocks: readonly Block[], nodeCap: number): SolveResult {
  const n = blocks.length;
  if (carSolved(blocks)) return { status: 'solved', moves: 0, path: [] };
  const carIndex = blocks.findIndex((b) => b.id === 0);
  const len = new Uint8Array(n);
  const horiz = new Uint8Array(n);
  const fixed = new Uint8Array(n);
  const ids = new Int32Array(n);
  blocks.forEach((b, i) => {
    len[i] = b.len;
    horiz[i] = b.horiz ? 1 : 0;
    fixed[i] = b.horiz ? b.y : b.x;
    ids[i] = b.id;
  });
  const goalPos = SIZE - (len[carIndex] as number);

  const pool = new Uint8Array((nodeCap + 2) * n);
  const parent = new Int32Array(nodeCap + 2);
  const moveBlock = new Int32Array(nodeCap + 2);
  const moveDelta = new Int8Array(nodeCap + 2);
  blocks.forEach((b, i) => {
    pool[i] = b.horiz ? b.x : b.y;
  });
  parent[0] = -1;

  const keyOf = (base: number): number => {
    let k = 0;
    for (let i = 0; i < n; i++) k = k * SIZE + (pool[base + i] as number);
    return k;
  };
  const visited = new Set<number>([keyOf(0)]);
  const occ = new Uint8Array(SIZE * SIZE);
  let head = 0;
  let tail = 1;

  const cell = (i: number, p: number): number =>
    horiz[i] ? (fixed[i] as number) * SIZE + p : p * SIZE + (fixed[i] as number);

  while (head < tail) {
    const base = head * n;
    occ.fill(255);
    for (let i = 0; i < n; i++) {
      const p = pool[base + i] as number;
      for (let k = 0; k < (len[i] as number); k++) occ[cell(i, p + k)] = i;
    }
    for (let i = 0; i < n; i++) {
      const p = pool[base + i] as number;
      const l = len[i] as number;
      for (let sign = -1; sign <= 1; sign += 2) {
        for (let step = 1; step <= SIZE; step++) {
          const np = p + sign * step;
          if (np < 0 || np + l > SIZE) break;
          // The cell the block newly covers must be free.
          const lead = sign > 0 ? np + l - 1 : np;
          if (occ[cell(i, lead)] !== 255) break;
          if (tail > nodeCap) return { status: 'unknown' };
          const cbase = tail * n;
          for (let j = 0; j < n; j++) pool[cbase + j] = pool[base + j] as number;
          pool[cbase + i] = np;
          const k = keyOf(cbase);
          if (visited.has(k)) continue;
          visited.add(k);
          parent[tail] = head;
          moveBlock[tail] = ids[i] as number;
          moveDelta[tail] = sign * step;
          if (i === carIndex && np === goalPos) {
            const path: EscapeMove[] = [];
            for (let t = tail; parent[t] !== -1; t = parent[t] as number) {
              path.push({ block: moveBlock[t] as number, delta: moveDelta[t] as number });
            }
            path.reverse();
            return { status: 'solved', moves: path.length, path };
          }
          tail++;
        }
      }
    }
    head++;
  }
  return { status: 'unsolvable' };
}
